import React, { useEffect, useState } from "react";
import { X, CheckCircle2, Loader2 } from "lucide-react";

/**
 * DemoRequestModal — replaces the legacy `mailto:` button so we can
 * actually capture contact intent in the database and surface a tidy
 * confirmation state instead of bouncing the user out to their mail
 * client. Posts to `POST /api/marketing/demo-request` (endpoint name
 * predates the landing page's shift away from demo-first messaging --
 * left unchanged since it's a real, working backend contract).
 *
 * Self-contained: holds its own form / loading / success state so the
 * landing page only needs to flip `open` and pass an `onClose`
 * handler.
 */
export default function DemoRequestModal({ open, onClose }) {
    const [form, setForm] = useState({
        name: "",
        work_email: "",
        company: "",
        phone: "",
        team_size: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState("");

    // Reset state every time the modal re-opens so a successful submit
    // doesn't leave the next visitor staring at someone else's confirmation.
    useEffect(() => {
        if (open) {
            setForm({ name: "", work_email: "", company: "", phone: "", team_size: "", message: "" });
            setDone(false);
            setErr("");
            setLoading(false);
        }
    }, [open]);

    // Esc to close + body-scroll lock while the modal is up.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && onClose?.();
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    if (!open) return null;

    const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const base = process.env.REACT_APP_BACKEND_URL || "";
            const r = await fetch(`${base}/api/marketing/demo-request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                setErr(data.detail || "Something went wrong. Please try again.");
                return;
            }
            setDone(true);
        } catch {
            setErr("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8"
            data-testid="demo-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="demo-modal-title"
        >
            <button
                type="button"
                aria-label="Close"
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
                data-testid="demo-modal-backdrop"
            />
            <div className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-white/10 bg-[#061b34] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00C2FF]/60 to-transparent" />
                <button
                    type="button"
                    aria-label="Close"
                    onClick={onClose}
                    data-testid="demo-modal-close"
                    className="absolute right-3 top-3 rounded-md border border-white/10 bg-white/[0.04] p-1.5 text-white/60 transition hover:text-white"
                >
                    <X size={16} />
                </button>
                {done ? (
                    <div className="px-7 py-10 text-center" data-testid="demo-modal-success">
                        <CheckCircle2 size={36} className="mx-auto text-[#10b981]" />
                        <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-white">
                            Message received
                        </h2>
                        <p className="mt-2 text-sm leading-relaxed text-white/70">
                            Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}. Our team will reach
                            out within one business day.
                        </p>
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="demo-modal-success-close"
                            className="mt-7 rounded-lg bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.22em] text-[#03122b] transition hover:bg-white/90"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <div className="px-7 py-7 sm:px-8 sm:py-8">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00C2FF]">
                            Contact us
                        </div>
                        <h2
                            id="demo-modal-title"
                            className="mt-2 font-display text-2xl font-bold tracking-tight text-white"
                        >
                            Planning a larger migration?
                        </h2>
                        <p className="mt-2 text-sm text-white/60">
                            Tell us what you're moving and we'll get back to you within one
                            business day.
                        </p>

                        <form onSubmit={submit} className="mt-6 space-y-3.5" data-testid="demo-form">
                            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                                <Field label="Full name *">
                                    <input
                                        type="text"
                                        required
                                        value={form.name}
                                        onChange={update("name")}
                                        data-testid="demo-name-input"
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Work email *">
                                    <input
                                        type="email"
                                        required
                                        value={form.work_email}
                                        onChange={update("work_email")}
                                        data-testid="demo-email-input"
                                        className={inputCls}
                                    />
                                </Field>
                            </div>
                            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                                <Field label="Company *">
                                    <input
                                        type="text"
                                        required
                                        value={form.company}
                                        onChange={update("company")}
                                        data-testid="demo-company-input"
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Phone *">
                                    <input
                                        type="tel"
                                        required
                                        value={form.phone}
                                        onChange={update("phone")}
                                        data-testid="demo-phone-input"
                                        className={inputCls}
                                    />
                                </Field>
                            </div>
                            <Field label="Team size">
                                <select
                                    value={form.team_size}
                                    onChange={update("team_size")}
                                    data-testid="demo-team-size-input"
                                    className={inputCls + " appearance-none"}
                                >
                                    <option value="">Pick one…</option>
                                    <option value="1-10">1 – 10</option>
                                    <option value="11-50">11 – 50</option>
                                    <option value="51-200">51 – 200</option>
                                    <option value="201-1000">201 – 1,000</option>
                                    <option value="1000+">1,000+</option>
                                </select>
                            </Field>
                            <Field label="What are you migrating?">
                                <textarea
                                    rows={3}
                                    value={form.message}
                                    onChange={update("message")}
                                    placeholder="e.g. 240 PCs from on-prem AD to Entra-joined endpoints"
                                    data-testid="demo-message-input"
                                    className={inputCls}
                                />
                            </Field>

                            {err && (
                                <div
                                    data-testid="demo-modal-error"
                                    className="rounded-lg border border-[#ef4444]/40 bg-[#ef4444]/10 px-3 py-2 text-xs text-[#fca5a5]"
                                >
                                    {err}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="demo-submit-btn"
                                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#00C2FF] to-[#5b5ef0] px-5 py-3 text-sm font-semibold uppercase tracking-[0.22em] text-white shadow-lg transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {loading && <Loader2 size={14} className="animate-spin" />}
                                {loading ? "Sending…" : "Send message"}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}

const inputCls =
    "w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white placeholder:text-white/30 outline-none transition focus:border-[#00C2FF]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#00C2FF]/20";

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-white/45">
                {label}
            </span>
            {children}
        </label>
    );
}
